/**
 * Estoque de demonstração. Espelha a semente da migração `0014`.
 *
 * O saldo de cada produto NÃO está aqui: ele é a soma das movimentações, como
 * no banco. Guardar os dois lados faria a lista de produtos e o extrato
 * discordarem na primeira entrada registrada.
 */

import type { StatusDaReserva } from './tipos';

export interface FornecedorSemente {
    id: string;
    nome: string;
    cnpj: string;
    contato: string;
    telefone: string;
    email: string;
    prazoEntrega: string;
    ativo: boolean;
    /** A categoria de conta padrão (0159). */
    categoriaId?: string;
}

export interface MarcaSemente {
    id: string;
    nome: string;
    fabricante: string;
    fornecedorId: string;
    categoria: string;
    registro?: string;
    ativa: boolean;
}

export interface CategoriaSemente {
    id: string;
    nome: string;
    descricao: string;
    ativa: boolean;
}

export interface ProdutoSemente {
    id: string;
    nome: string;
    categoriaId: string;
    marcaId: string;
    fornecedorId: string;
    lote: string;
    validade: string;
    minimo: number;
    unidade: string;
    conteudo: number;
    unidadeConteudo: string;
    unidadeConsumo: string;
    custoCentavos: number;
    ativo: boolean;
}

export interface MovimentacaoSemente {
    id: string;
    produtoId: string;
    tipo: 'entrada' | 'saida';
    quantidade: number;
    quantidadeConsumo?: number;
    unidadeConsumo?: string;
    origem: string;
    ocorridoEm: string;
    reservaId?: string;
}

export interface ReservaSemente {
    id: string;
    procedimentoId?: string;
    procedimento: string;
    contatoId?: string;
    paciente: string;
    origem: string;
    status: StatusDaReserva;
    criadoEm: string;
    itens: { produtoId: string; quantidadeConsumo: number; unidadeConsumo: string }[];
}

export const FORNECEDORES: FornecedorSemente[] = [
    { id: 'for-1', nome: 'Distribuidora MedSul', cnpj: '12.884.220/0001-40', contato: 'Rogério Lima', telefone: '(11) 3444-2210', email: 'vendas@medsul.com.br', prazoEntrega: '3 dias úteis', ativo: true },
    { id: 'for-2', nome: 'Derma Supply', cnpj: '08.221.774/0001-15', contato: 'Fabiana Reis', telefone: '(11) 3777-8890', email: 'contato@dermasupply.com.br', prazoEntrega: '5 dias úteis', ativo: true },
    { id: 'for-3', nome: 'Volume Estética', cnpj: '21.550.902/0001-88', contato: 'Marcos Tavares', telefone: '(11) 3112-4455', email: 'pedidos@volumeestetica.com.br', prazoEntrega: '7 dias úteis', ativo: false },
];

export const CATEGORIAS_DE_PRODUTO: CategoriaSemente[] = [
    { id: 'cpr-1', nome: 'Injetáveis', descricao: 'Toxina, preenchedores e bioestimuladores', ativa: true },
    { id: 'cpr-2', nome: 'Cosméticos e ativos', descricao: 'Ácidos, máscaras e anestésicos tópicos', ativa: true },
    { id: 'cpr-3', nome: 'Descartáveis', descricao: 'Agulhas, seringas, luvas e campos', ativa: true },
    { id: 'cpr-4', nome: 'Equipamentos e acessórios', descricao: 'Ponteiras e peças de reposição', ativa: true },
    /* Inativa de propósito: é o caso que prova que categoria fora de uso some
       do cadastro sem levar junto o produto que já a usa. */
    { id: 'cpr-5', nome: 'Materiais de expediente', descricao: 'Uso administrativo, fora do consumo clínico', ativa: false },
];

export const MARCAS: MarcaSemente[] = [
    { id: 'mar-1', nome: 'Botulift', fabricante: 'Botulift Pharma', fornecedorId: 'for-1', categoria: 'Toxina botulínica', registro: 'ANVISA 8012.4471', ativa: true },
    { id: 'mar-2', nome: 'Volumax', fabricante: 'Volumax Labs', fornecedorId: 'for-3', categoria: 'Preenchedor', registro: 'ANVISA 8012.9920', ativa: true },
    { id: 'mar-3', nome: 'DermaPro', fabricante: 'DermaPro Cosméticos', fornecedorId: 'for-2', categoria: 'Ácidos e peelings', registro: 'ANVISA 2551.0088', ativa: true },
    { id: 'mar-4', nome: 'SkinCare', fabricante: 'SkinCare Brasil', fornecedorId: 'for-2', categoria: 'Cosmético', ativa: true },
    { id: 'mar-5', nome: 'MedFine', fabricante: 'MedFine Descartáveis', fornecedorId: 'for-1', categoria: 'Descartáveis', ativa: true },
    { id: 'mar-6', nome: 'Lidocare', fabricante: 'Lidocare Farma', fornecedorId: 'for-3', categoria: 'Anestésico', registro: 'ANVISA 1180.7712', ativa: false },
];

export const PRODUTOS: ProdutoSemente[] = [
    { id: 'prd-1', nome: 'Toxina botulínica 100 UI', categoriaId: 'cpr-1', marcaId: 'mar-1', fornecedorId: 'for-1', lote: 'BT-2291', validade: '2026-12-01', minimo: 5, unidade: 'frasco', conteudo: 100, unidadeConteudo: 'ui', unidadeConsumo: 'ui', custoCentavos: 89000, ativo: true },
    { id: 'prd-2', nome: 'Ácido hialurônico 1 ml', categoriaId: 'cpr-1', marcaId: 'mar-2', fornecedorId: 'for-3', lote: 'AH-1140', validade: '2027-04-01', minimo: 6, unidade: 'seringa', conteudo: 1, unidadeConteudo: 'ml', unidadeConsumo: 'ul', custoCentavos: 64000, ativo: true },
    { id: 'prd-3', nome: 'Ácido glicólico 70%', categoriaId: 'cpr-2', marcaId: 'mar-3', fornecedorId: 'for-2', lote: 'AG-0072', validade: '2026-10-01', minimo: 4, unidade: 'frasco', conteudo: 200, unidadeConteudo: 'ml', unidadeConsumo: 'ml', custoCentavos: 18000, ativo: true },
    { id: 'prd-4', nome: 'Máscara calmante', categoriaId: 'cpr-2', marcaId: 'mar-4', fornecedorId: 'for-2', lote: 'MC-5510', validade: '2028-02-01', minimo: 20, unidade: 'und', conteudo: 1, unidadeConteudo: 'und', unidadeConsumo: 'und', custoCentavos: 2200, ativo: true },
    { id: 'prd-5', nome: 'Agulha 30G', categoriaId: 'cpr-3', marcaId: 'mar-5', fornecedorId: 'for-1', lote: 'AG-3001', validade: '2029-09-01', minimo: 100, unidade: 'und', conteudo: 1, unidadeConteudo: 'und', unidadeConsumo: 'und', custoCentavos: 120, ativo: true },
    { id: 'prd-6', nome: 'Anestésico tópico', categoriaId: 'cpr-2', marcaId: 'mar-6', fornecedorId: 'for-3', lote: 'AN-7712', validade: '2026-11-01', minimo: 3, unidade: 'tubo', conteudo: 30, unidadeConteudo: 'g', unidadeConsumo: 'g', custoCentavos: 7600, ativo: true },
];

/* O saldo inicial é o de ANTES das movimentações de setembro: quatro frascos
   de toxina menos a saída de um deixam os três que a tela mostra. */
export const MOVIMENTACOES_DE_ESTOQUE: MovimentacaoSemente[] = [
    { id: 'mve-1', produtoId: 'prd-1', tipo: 'entrada', quantidade: 4, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-2', produtoId: 'prd-2', tipo: 'entrada', quantidade: 12, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-3', produtoId: 'prd-3', tipo: 'entrada', quantidade: 3, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-4', produtoId: 'prd-4', tipo: 'entrada', quantidade: 24, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-5', produtoId: 'prd-5', tipo: 'entrada', quantidade: 320, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-6', produtoId: 'prd-6', tipo: 'entrada', quantidade: 1, origem: 'Saldo inicial', ocorridoEm: '2026-08-01T09:00:00-03:00' },
    { id: 'mve-7', produtoId: 'prd-3', tipo: 'saida', quantidade: 1, origem: 'Atendimento — Helena Vasques', ocorridoEm: '2026-09-02T10:30:00-03:00' },
    { id: 'mve-8', produtoId: 'prd-4', tipo: 'entrada', quantidade: 24, origem: 'Derma Supply · NF 88214', ocorridoEm: '2026-09-03T14:00:00-03:00' },
    { id: 'mve-9', produtoId: 'prd-1', tipo: 'saida', quantidade: 1, origem: 'Atendimento — Júlia Bastos', ocorridoEm: '2026-09-05T09:40:00-03:00' },
];

/* Uma reserva aberta: é ela que faz a aba de Reservas ter o que mostrar e o
   disponível do produto ficar menor que a quantidade em estoque. */
export const RESERVAS: ReservaSemente[] = [
    {
        id: 'res-1',
        procedimentoId: 'prc-5',
        procedimento: 'Toxina botulínica',
        contatoId: 'p-julia',
        paciente: 'Júlia Bastos',
        origem: 'Proposta aceita',
        status: 'reservada',
        criadoEm: '2026-09-08T11:20:00-03:00',
        itens: [
            { produtoId: 'prd-1', quantidadeConsumo: 50, unidadeConsumo: 'ui' },
            { produtoId: 'prd-5', quantidadeConsumo: 4, unidadeConsumo: 'und' },
            { produtoId: 'prd-6', quantidadeConsumo: 3, unidadeConsumo: 'g' },
        ],
    },
];
